import { named, withDependencies } from '@wix/thunderbolt-ioc'
import { IViewerManagerImplementor } from 'thunderbolt-viewer-manager-types'
import type { DSCarmi } from 'thunderbolt-ds-carmi-root'
import {
	CarmiInstance,
	BrowserWindow,
	BrowserWindowSymbol,
	CompsLifeCycleSym,
	ICompsLifeCycle,
	IPageDidMountHandler,
	ExperimentsSymbol,
	Experiments,
	EditorFeatureConfigSymbol,
	IPageWillMountHandler,
} from '@wix/thunderbolt-symbols'
import { name, VariantsApiSymbol } from './symbols'
import { ViewerManagerVariantsApiEditorConfig, ViewerManagerVariantsApiExtension } from './types'
import { getFullId, getUniqueId, getMultipleRefCompsIdFromInflatedId } from '@wix/thunderbolt-commons'
import { Catharsis, CatharsisSymbol } from '@wix/thunderbolt-catharsis'
import {
	ActiveVariantsApiSymbol,
	IActiveVariantsApi,
	ClassNameApiSymbol,
	IClassNameApi,
} from 'feature-triggers-and-reactions'
import { isInflatedId } from '@wix/thunderbolt-byref'

export const ViewerManagerVariantsApi = withDependencies(
	[
		CompsLifeCycleSym,
		BrowserWindowSymbol,
		ActiveVariantsApiSymbol,
		ClassNameApiSymbol,
		CarmiInstance,
		CatharsisSymbol,
		ExperimentsSymbol,
	],
	(
		compsLifeCycle: ICompsLifeCycle,
		window: NonNullable<BrowserWindow>,
		activeVariantsApi: IActiveVariantsApi,
		{ addClassName, removeClassName }: IClassNameApi,
		{ variants: variantsSetters, $runInBatch }: DSCarmi,
		catharsis: Catharsis,
		experiments: Experiments
	): IViewerManagerImplementor<'variants'> & ViewerManagerVariantsApiExtension => {
		const lockedVariants: { [compId: string]: true } = {}
		let lockingVariant = false

		const setAndLockVariantForComp = (compId: string, variantId: string) => {
			lockingVariant = true
			setVariantForComp(compId, variantId)

			lockedVariants[compId] = true
			lockingVariant = false
		}

		const setVariantForComp = (compId: string, variantId: string | undefined) => {
			if (lockedVariants[compId]) {
				return
			}

			const hasInflatedVariant = isInflatedId(compId) && variantId && !isInflatedId(variantId)

			const fixedVariantId = hasInflatedVariant
				? getUniqueId(getMultipleRefCompsIdFromInflatedId(compId), variantId)
				: variantId

			const treatHtmlElement = () => {
				const activeVariant = getActiveVariant(compId)
				if (activeVariant) {
					removeClassName(compId, activeVariant)
				}

				if (fixedVariantId) {
					addClassName(compId, fixedVariantId)
				}
			}

			const element = window.document.getElementById(compId)
			if (element) {
				treatHtmlElement()
			} else {
				compsLifeCycle.waitForComponentToRender(compId).then(treatHtmlElement)
			}

			if (fixedVariantId) {
				activeVariantsApi.setActiveVariant(compId, fixedVariantId, lockingVariant)
			} else {
				activeVariantsApi.resetActiveVariants(compId)
			}
		}

		const getActiveVariant = (compId: string) => activeVariantsApi.getActiveVariant(compId)

		const setCompsIgnoredTransformProps = (compIds: Array<string>, ignoredTransformProps?: Array<string>) => {
			if (experiments['specs.thunderbolt.catharsis_transformations_style']) {
				catharsis.transaction(({ updateEnvironment }) => {
					updateEnvironment({
						compIdToIgnoredTransformProps: compIds.reduce(
							(acc, compId) => ({
								...acc,
								[getFullId(compId)]: ignoredTransformProps,
							}),
							{}
						),
					})
				})
				return
			}

			$runInBatch(() =>
				compIds.forEach((compId) => {
					const id = getFullId(compId)
					variantsSetters.setCompIgnoredTransformProps(id, ignoredTransformProps)
				})
			)
		}

		const setGlobalIgnoredTransformProps = (ignoredTransformProps: Array<string>) => {
			if (experiments['specs.thunderbolt.catharsis_transformations_style']) {
				catharsis.transaction(({ updateEnvironment }) =>
					updateEnvironment({ globalIgnoredTransformProps: ignoredTransformProps })
				)
			} else {
				variantsSetters.setGlobalIgnoredTransformProps(ignoredTransformProps)
			}
		}

		const setEnableVariantsTransitionsInEditor = (enableTransitions: boolean) => {
			if (experiments['specs.thunderbolt.catharsis_transitions_style']) {
				catharsis.transaction(({ updateEnvironment }) =>
					updateEnvironment({ enableVariantsTransitionsInEditor: enableTransitions })
				)
			} else {
				variantsSetters.setEnableVariantsTransitionsInEditor(enableTransitions)
			}
		}

		const isDuringInteractionMode = () => activeVariantsApi.hasActiveVariants()

		return {
			variants: {
				setVariantForComp,
				getActiveVariant,
				setCompsIgnoredTransformProps,
				setGlobalIgnoredTransformProps,
				setEnableVariantsTransitionsInEditor,
			},
			isDuringInteractionMode,
			setAndLockVariantForComp,
		}
	}
)

export const VariantsApiPageHandler = withDependencies(
	[
		named(EditorFeatureConfigSymbol, name),
		ActiveVariantsApiSymbol,
		VariantsApiSymbol,
		ClassNameApiSymbol,
		CarmiInstance,
		CatharsisSymbol,
	],
	(
		editorConfig: ViewerManagerVariantsApiEditorConfig,
		activeVariantsApi: IActiveVariantsApi,
		{ setAndLockVariantForComp }: ViewerManagerVariantsApiExtension,
		{ addClassName }: IClassNameApi,
		{ variants: setter }: DSCarmi,
		catharsis: Catharsis
	): IPageWillMountHandler & IPageDidMountHandler => {
		return {
			name: 'variantsApiPageHandler',
			pageWillMount(pageId) {
				if (pageId !== 'masterPage' && editorConfig.presetIdOverride) {
					setAndLockVariantForComp(pageId, editorConfig.presetIdOverride)
				}

				const compVariantMap = activeVariantsApi.getActiveVariants()
				const compVariant = compVariantMap[pageId]

				if (compVariant && compVariant !== activeVariantsApi.getCurrentPageActiveVariant()) {
					activeVariantsApi.setCurrentPageActiveVariant(compVariant)

					setter.setActiveVariant(compVariant)
					catharsis.transaction(({ updateEnvironment }) => updateEnvironment({ activeVariant: compVariant }))
				}
			},
			pageDidMount() {
				// this is needed for cases where setVariantForComp was called before the page was mounted.
				const compVariantMap = activeVariantsApi.getActiveVariants()
				Object.entries(compVariantMap).forEach(([compId, variantId]) => {
					const element = window.document.getElementById(compId)
					if (element) {
						addClassName(compId, variantId)
					}
				})
			},
		}
	}
)
